<template>
    <el-dialog :title="isPmRule ? '品名 MSKU 规则' : '父体 MSKU 规则'" :visible.sync="visibleSync" width="650px" append-to-body
        :close-on-click-modal="false" @open="onOpen">
        <el-alert type="info" :closable="false" show-icon style="margin-bottom:14px;"
            title="这里只维护 MSKU 的基础名规则。不会保存产品类型、创建字段、移入或移出字段。" />
        <el-form label-width="125px" size="small">
            <el-form-item label="本地父体" required>
                <el-select v-model="form.parent" filterable placeholder="选择本地父体" style="width:100%"
                    @change="loadRule" :disabled="isPmRule">
                    <el-option v-for="item in parentOptions" :key="item.value"
                        :label="item.label" :value="item.value" />
                </el-select>
            </el-form-item>
            <el-form-item v-if="isPmRule" label="本地品名"><el-input :value="form.pm" disabled /></el-form-item>
            <el-form-item v-if="isPmRule" label="店铺"><el-input :value="form.shop" disabled /></el-form-item>
            <el-form-item label="生成模式">
                <el-radio-group v-model="form.use_msku_template">
                    <el-radio :label="false">按属性拼接</el-radio>
                    <el-radio :label="true">按模板生成</el-radio>
                </el-radio-group>
            </el-form-item>
            <el-form-item label="MSKU模板" v-if="form.use_msku_template">
                <el-input v-model="form.msku_template" placeholder="如：CB-KPW5" />
            </el-form-item>
            <template v-else>
                <el-form-item label="系列"><el-input v-model="form.series" placeholder="如：KPW5" /></el-form-item>
                <el-form-item label="款式"><el-input v-model="form.style" placeholder="如：Classic" /></el-form-item>
                <el-form-item label="分隔符"><el-input v-model="form.msku_separator" style="width:120px" /></el-form-item>
                <div class="hint">属性基础名：系列 + 款式 + MSKU颜色。</div>
            </template>
            <el-form-item v-if="isPmRule" label="MSKU颜色" :required="!form.use_msku_template">
                <el-input v-model="form.msku_color" placeholder="配置品名默认色，如：墨绿色" />
            </el-form-item>
            <div v-if="isPmRule" class="hint">保存后写入该品名配置的 msku_color。上传表格填写的颜色只覆盖本次生成行，确认“下载 Excel 并保存”后才会更新为该品名的默认颜色。</div>
            <el-form-item label="国家代码" required>
                <el-select v-model="form.country" filterable placeholder="选择 ISO 两位代码" style="width:100%">
                    <el-option v-for="code in countryCodes" :key="code" :label="code" :value="code" />
                </el-select>
            </el-form-item>
        </el-form>
        <div class="hint">供应商按每行 SKU 对应的 ERP 本地品名末段自动带出，可在生成表格中修改；不会保存在品名配置中。</div>
        <div class="hint">最终格式：基础名-供应商-国家缩写-MMDD随机数序号-后缀；超过 40 位时只截断基础名。</div>
        <span slot="footer">
            <el-button @click="visibleSync = false">取消</el-button>
            <el-button type="primary" :loading="saving" @click="save">保存 MSKU 规则</el-button>
        </span>
    </el-dialog>
</template>

<script>
export default {
    name: 'MskuRuleDialog',
    props: {
        visible: { type: Boolean, default: false },
        parent: { type: String, default: '' },
        pm: { type: String, default: '' },
        shop: { type: String, default: '' },
        parentList: { type: Array, default: () => [] },
    },
    data() {
        return {
            saving: false,
            form: this.blankForm(),
        };
    },
    computed: {
        visibleSync: {
            get() { return this.visible; },
            set(value) { this.$emit('update:visible', value); },
        },
        parentOptions() {
            return (this.parentList || []).map(item => ({
                value: item['本地父体'] || '', label: item['本地父体'] || '',
            })).filter(item => item.value);
        },
        isPmRule() { return !!(this.pm && this.shop); },
        countryCodes() { return ['AE', 'AU', 'BE', 'BR', 'CA', 'DE', 'ES', 'FR', 'IN', 'IT', 'JP', 'MX', 'NL', 'PL', 'SA', 'SE', 'SG', 'TR', 'UK', 'US']; },
    },
    methods: {
        blankForm() {
            return {
                parent: '', pm: '', shop: '', use_msku_template: false, msku_template: '', series: '', style: '',
                msku_separator: '-', country: '', msku_color: '',
            };
        },
        onOpen() {
            this.form = this.blankForm();
            this.form.parent = this.parent || '';
            this.form.pm = this.pm || '';
            this.form.shop = this.shop || '';
            if (this.form.parent) this.loadRule();
        },
        loadRule() {
            if (!this.form.parent) return;
            const url = this.isPmRule ? '/vue/msku-generation/pm-rule' : '/vue/msku-generation/parent-rule';
            const params = this.isPmRule
                ? { parent: this.form.parent, pm: this.form.pm, shop: this.form.shop }
                : { parent: this.form.parent };
            this.$axios.get(url, {
                params,
            }).then(res => {
                if (res.code === 200 && res.data) {
                    this.form = Object.assign(this.blankForm(), res.data);
                } else {
                    this.$message.error((res && res.message) || 'MSKU 规则读取失败');
                }
            }).catch(() => this.$message.error('MSKU 规则读取失败'));
        },
        save() {
            this.form.country = String(this.form.country || '').trim().toUpperCase();
            if (!this.form.parent || !this.form.country) {
                return this.$message.warning('请填写本地父体和国家代码');
            }
            if (!this.countryCodes.includes(this.form.country)) return this.$message.warning('国家代码必须为已支持的 ISO 两位英文代码');
            if (this.form.use_msku_template && !this.form.msku_template) {
                return this.$message.warning('模板模式必须填写 MSKU模板');
            }
            if (this.isPmRule && !this.form.use_msku_template && !this.form.msku_color) {
                return this.$message.warning('属性模式必须填写品名的配置颜色');
            }
            this.saving = true;
            const url = this.isPmRule ? '/vue/msku-generation/pm-rule' : '/vue/msku-generation/parent-rule';
            this.$axios.post(url, this.form).then(res => {
                if (res.code === 200) {
                    this.$message.success('MSKU 规则已保存');
                    this.$emit('saved');
                    this.visibleSync = false;
                } else {
                    this.$message.error((res && res.message) || '保存失败');
                }
            }).catch(() => this.$message.error('MSKU 规则保存失败')).finally(() => {
                this.saving = false;
            });
        },
    },
};
</script>

<style scoped>
.hint { color: #909399; font-size: 12px; line-height: 1.6; margin: -8px 0 12px 125px; }
</style>
